import {
	AccordionAudience,
	Button,
	Input,
	MenuTabItem,
	Typography,
	handleValid
} from 'components'
import { SetUpInfoTab } from 'components/setUpInfoTab'
import { TabManagerCards } from 'components/tabManageCards'
import {
	DynamicTapContentProps,
	Icons,
	Reach,
	TabMyOrganizationItem,
	TypographyTypes
} from 'interfaces'
import React, { useEffect, useState } from 'react'
import { Controller, Path, useFieldArray, useWatch } from 'react-hook-form'
import { ElementComponent } from './elementComponent'

export interface ropsTotalList {
	tags: number[] | undefined
}

export const DynamicTabContent: React.FC<DynamicTapContentProps> = ({
	data,
	active,
	deletedTab,
	control,
	setValue,
	register,
	watch,
	errors,
	isValid,
	saveData,
	errorForm,
	setErrorForm,
	fieldError,
	setFieldError,
	nameSection,
	resourceUpdated
}): JSX.Element => {
	const hasAudience = data && data.audience.tags.length > 0
	const nameType = `tabs.${active}`
	const dataInfoData = watch()
	const nameModule = `tabs.${active}.name`
	const [publishMyPeope, setPublishMyPeope] = useState<string>('1')
	const [totalList, setTotalList] = useState<Reach>({
		tags: data?.audience?.tags ?? [],
		lists: [],
		dynamic_lists: [],
		individuals: [],
		organizations: [],
		feeds: [],
		partner_lists: [],
		organization_id: `${data?.organization_id}`
	})

	const { fields, remove } = useFieldArray({
		control,
		name: `tabs.${active}.components`
	})

	const enabledLink = useWatch({
		control,
		name: `tabs.${active}.setup_card.link.enabled_link`
	})

	const enabledMenu = useWatch({
		control,
		name: `tabs.${active}.setup_card.menu.enabled_menu`
	})

	useEffect(() => {
		setValue(`tabs.${active}.audience.tags`, totalList.tags)
	}, [totalList])

	useEffect(() => {
		if (publishMyPeope === '2' && totalList.tags.length === 0) {
			handleValid(nameModule, false, setFieldError)
		} else {
			handleValid(nameModule, true, setFieldError)
		}
	}, [publishMyPeope, totalList, fields])

	useEffect(() => {
		if (hasAudience) {
			setPublishMyPeope('2')
		}
	}, [data])

	const imageCard =
		watch(
			`${nameType}.image_card` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? ''
	const linkInfo =
		watch(
			`${nameType}.setup_card.link.enabled_link` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? false
	const urlInfo =
		watch(
			`${nameType}.setup_card.link.url` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? ''
	const components =
		watch(
			`${nameType}.components` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? []
	const enabledMenus =
		watch(
			`${nameType}.setup_card.menu.enabled_menu` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? false
	const setUpMenu =
		watch(
			`${nameType}.setup_menu` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? []

	const validateMenuData = (): boolean => {
		if (!imageCard && resourceUpdated) {
			return false
		}
		if (linkInfo && !String(urlInfo).trim() && resourceUpdated) {
			return false
		}
		if (
			enabledMenus &&
			Array.isArray(setUpMenu) &&
			setUpMenu.length === 0 &&
			resourceUpdated
		) {
			return false
		}

		if (
			!linkInfo &&
			!enabledMenus &&
			Array.isArray(components) &&
			components.length === 0
		) {
			return false
		}

		return true
	}

	useEffect(() => {
		const isValidMenu = validateMenuData()
		if (fieldError.includes(nameType) && isValidMenu) {
			handleValid(nameType, true, setFieldError)
		} else if (!fieldError.includes(nameType) && !isValidMenu) {
			handleValid(nameType, false, setFieldError)
		}
	}, [
		dataInfoData,
		linkInfo,
		urlInfo,
		components,
		enabledMenus,
		setUpMenu,
		imageCard,
		isValid
	])

	return (
		<div className="md:mt-5 md:flex-row flex flex-col gap-4 pt-4">
			<div className="md:w-4/12 w-full">
				<TabManagerCards
					active={active}
					register={register}
					errors={errors}
					setValue={setValue}
					watch={watch}
					deletedTab={deletedTab}
					control={control}
					remove={remove}
					fieldError={fieldError}
					setFieldError={setFieldError}
					nameSection={nameSection}
					resourceUpdated={resourceUpdated}
				/>
			</div>
			<div className="md:w-8/12 w-full">
				<div
					className="w-full px-4 pb-6 rounded-lg bg-white"
					style={{ boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.1)' }}
				>
					<div className="w-full py-3  mb-3 flex justify-between items-center">
						<Typography
							title="Tab visibility:"
							fontLeading="12/12"
							type={TypographyTypes.p}
							className="text-gray-8"
						/>
					</div>

					<AccordionAudience
						active={active}
						publishMyPeope={publishMyPeope}
						setPublishMyPeope={setPublishMyPeope}
						totalList={totalList}
						setTotalList={setTotalList}
						defaultOpen={hasAudience}
						defaultOpenParent
					/>
					{enabledLink && resourceUpdated && (
						<div className="px-4 pb-6 my-4 rounded-lg bg-white border-[1px] border-blue-primary">
							<Controller
								control={control}
								name={`tabs.${active}.setup_card.link.enabled_link`}
								render={({ field: { value } }) => {
									return (
										<>
											<SetUpInfoTab
												title="Set external URL:"
												subTitle="External Redirection"
												icon={Icons.share}
												value={value}
											/>
											<Input
												name={`tabs.${active}.setup_card.link.url`}
												register={register}
												rules={{
													required: {
														value: enabledLink,
														message: 'Is required'
													},
													pattern: {
														value: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
														message: 'Please enter a valid url'
													},
													maxLength: {
														value: 512,
														message: 'URL too large. Maximum 512 characters.'
													}
												}}
												error={errors?.tabs?.[active]?.setup_card?.link?.url}
												borderFull={false}
												inputClassName="rounded-lg border-[1px] border-gray-17"
												autoFocus
											/>
										</>
									)
								}}
							/>
						</div>
					)}
					{!enabledLink && !enabledMenu && (
						<div>
							<ElementComponent
								name={`tabs.${active}.components`}
								active={active}
								nameCard={`${nameSection} Components:`}
								register={register}
								errors={errors}
								setValue={setValue}
								watch={watch}
								control={control}
								saveData={saveData}
								setFieldError={setFieldError}
								setErrorForm={setErrorForm}
								isValid={isValid}
								fieldError={fieldError}
								resourceUpdated={resourceUpdated}
							/>
						</div>
					)}
					{enabledMenu && resourceUpdated && (
						<MenuTabItem
							active={active}
							register={register}
							errors={errors}
							setValue={setValue}
							watch={watch}
							control={control}
							saveData={saveData}
							setFieldError={setFieldError}
							setErrorForm={setErrorForm}
							isValid={isValid}
							fieldError={fieldError}
						/>
					)}
					<div className="flex justify-end">
						<Button
							label="Save"
							onClick={() => {
								setValue(
									`tabs.${active}.audience.tags`,
									publishMyPeope === '2' ? totalList.tags : []
								)
								saveData()
							}}
							disabled={errorForm || !isValid || fieldError?.length > 0}
							className="w-full md:w-auto"
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
